import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, notification } from "antd";
import { observer } from "mobx-react-lite";

import { api } from "../../api";
import yandexLatinIconUrl from "../../assets/img/yandex-icon-latin-white.svg";
import yandexRuIconUrl from "../../assets/img/yandex-icon-ru-white.svg";
import { userStore } from "../../stores/userStore";
import { usePendingAction } from "../../utils/usePendingAction";

const CYRILLIC_LANGUAGES = new Set(["ru", "be", "bg"]);

export const YandexSocialAccount = observer(function YandexSocialAccount() {
  const { t } = useTranslation();
  const { pending: unlinkLoading, run: runUnlink } = usePendingAction();
  const linkedAccount =
    userStore.profile?.integrations?.social_list?.find(
      (account) => account.id === "yandex",
    );
  const isLinked = linkedAccount !== undefined;
  
  const hasRussianEmailDomain =
    linkedAccount?.value?.trim().toLowerCase().endsWith(".ru") ?? false;
  
    const accountCenterUrl =
    userStore.profile?.country === "RU" || hasRussianEmailDomain
      ? "https://id.yandex.ru/"
      : "https://id.yandex.com/";

  let iconUrl = yandexLatinIconUrl;

  if (CYRILLIC_LANGUAGES.has(userStore.profile?.locale ?? "")) {
    iconUrl = yandexRuIconUrl;
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isYandexAlreadyBound =
      params.get("oauth_account") === "already-bound" &&
      params.get("provider") === "yandex";

    if (isYandexAlreadyBound) {
      notification.error({
        message: t("profile.socialAccounts.alreadyBound"),
      });
      const url = new URL(window.location.href);
      url.searchParams.delete("oauth_account");
      url.searchParams.delete("provider");
      window.history.replaceState(null, "", url.toString());
      return;
    }

    if (sessionStorage.getItem("pendingSocialLink") !== "yandex") return;
    sessionStorage.removeItem("pendingSocialLink");
    if (isLinked) {
      notification.success({
        message: t("profile.socialAccounts.yandexLinkSuccess"),
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUnlink() {
    await runUnlink(async () => {
      try {
        await api.integration.unlinkYandex();
        const updatedProfile = await api.profile.getProfile();
        userStore.setProfile(updatedProfile);
        notification.success({
          message: t("profile.socialAccounts.yandexUnlinkSuccess"),
        });
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      }
    });
  }

  const icon = (
    <span className="social-accounts__icon">
      <img src={iconUrl} width={22} height={22} alt="Yandex" />
    </span>
  );

  if (isLinked) {
    return (
      <div className="social-accounts__row">
        <a
          className="social-accounts__chip"
          href={accountCenterUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={
            linkedAccount.value === null
              ? "Yandex"
              : `Yandex: ${linkedAccount.value}`
          }
        >
          {icon}
          {linkedAccount.value !== null && (
            <span className="social-accounts__chip-value">
              {linkedAccount.value}
            </span>
          )}
        </a>
        <Button size="large" loading={unlinkLoading} onClick={handleUnlink}>
          {t("profile.socialAccounts.unlink")}
        </Button>
      </div>
    );
  }

  return (
    <div className="social-accounts__row">
      {icon}
      <Button
        size="large"
        style={{ height: 36 }}
        onClick={() => {
          sessionStorage.setItem("pendingSocialLink", "yandex");
          window.location.href = "/yandex/login/?next=/profile";
        }}
      >
        {t("profile.socialAccounts.link")}
      </Button>
    </div>
  );
});
